import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import LecturesSubNav from '../components/LecturesSubNav'
import AppExcelUploadView from '../../../components/AppExcelUploadView'
import { downloadSyllabusTemplate, uploadSyllabusExcel } from '../syllabus/api/syllabusApi'
import { downloadLectureTemplate, uploadLectureExcel } from '../lectures-list/api/lectureApi'
import { appToast } from '../../../components/AppToast'

export default function UploadScreen() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const [activeTab, setActiveTab] = useState(tabParam === 'syllabus' ? 'syllabus' : 'lectures')

  useEffect(() => {
    const current = searchParams.get('tab')
    if (current === 'syllabus' || current === 'lectures') {
      setActiveTab(current)
    }
  }, [searchParams])

  const handleTabChange = (newTab) => {
    setActiveTab(newTab)
    setSearchParams({ tab: newTab })
  }

  const handleSyllabusUpload = async (file) => {
    try {
      const result = await uploadSyllabusExcel(file)
      if (result.success) {
        appToast.success(`Successfully imported ${result.recordsCreated} syllabus chapters!`)
        navigate('/lectures/syllabus')
      } else {
        const errCount = result.invalidRows || result.errors?.length || 0
        appToast.error(
          `Uploaded with ${errCount} errors. ${result.recordsCreated} records created. Check console for details.`
        )
        if (result.errors?.length) {
          console.table(result.errors)
        }
      }
    } catch (err) {
      console.error('Error importing syllabus file:', err)
      const detail = err.response?.data?.detail || err.message || 'Failed to import syllabus Excel file.'
      appToast.error(detail)
    }
  }

  const handleLectureUpload = async (file) => {
    try {
      const result = await uploadLectureExcel(file)
      if (result.success) {
        appToast.success(`Successfully imported ${result.recordsCreated} lectures!`)
        navigate('/lectures/list')
      } else {
        const errCount = result.invalidRows || result.errors?.length || 0
        appToast.error(
          `Uploaded with ${errCount} errors. ${result.recordsCreated} records created. Check console for details.`
        )
        if (result.errors?.length) {
          console.table(result.errors)
        }
      }
    } catch (err) {
      console.error('Error importing lectures file:', err)
      const detail = err.response?.data?.detail || err.message || 'Failed to import lecture Excel file.'
      appToast.error(detail)
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* ── Top Header & SubNav Canopy ── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Administration &amp; Tools</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Bulk Excel Importer
          </h1>
        </div>

        {/* ── Type Selector Tabs ── */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleTabChange('lectures')}
            className={`flex items-center gap-2 rounded-2xl px-5 py-2.5 text-xs font-bold transition shadow-2xs ${
              activeTab === 'lectures'
                ? 'bg-primary text-on-primary shadow-sm'
                : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined text-base">co_present</span>
            <span>Upload Lectures</span>
          </button>

          <button
            type="button"
            onClick={() => handleTabChange('syllabus')}
            className={`flex items-center gap-2 rounded-2xl px-5 py-2.5 text-xs font-bold transition shadow-2xs ${
              activeTab === 'syllabus'
                ? 'bg-primary text-on-primary shadow-sm'
                : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined text-base">auto_stories</span>
            <span>Upload Syllabus</span>
          </button>
        </div>
      </section>

      {/* ── Upload Card ── */}
      {activeTab === 'lectures' ? (
        <AppExcelUploadView
          title="Upload Lectures"
          subtitle="Import multiple lecture sessions, timetables, and teacher assignments."
          description="Use this option to upload lectures using Excel Sheets in 3 simple steps. Download a sample file, fill details and upload the file. Please make sure that all Master Values (Ex. Faculty, Course, Subject, Branch) are entered properly."
          sampleFileName="Import_Lectures_Template.xlsx"
          cancelPath="/lectures/list"
          backLabel="Back to Lectures List"
          masterRequirements={['Course Name', 'Subject Name', 'Faculty Name', 'Branch Names', 'Batch Names']}
          onDownloadSample={downloadLectureTemplate}
          onUpload={handleLectureUpload}
        />
      ) : (
        <AppExcelUploadView
          title="Upload Syllabus"
          subtitle="Import curriculum topics, chapter sequences, and duration estimates."
          description="Use this option to upload Syllabus using Excel Sheets in 3 simple steps. Download a sample file, fill details and upload the file. Please make sure that all Master Values (Ex. Course, Subjects, School/College Names) are created and entered properly."
          sampleFileName="Import_Syllabus_Template.xlsx"
          cancelPath="/lectures/syllabus"
          backLabel="Back to Syllabus List"
          masterRequirements={['Academic Year', 'Course', 'Subject', 'School / College']}
          onDownloadSample={downloadSyllabusTemplate}
          onUpload={handleSyllabusUpload}
        />
      )}
    </div>
  )
}
